import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AppNavItem } from '../../services/menu-mapping';
import { NavigationService } from '../../services/navigation.service';

/**
 * Tracking automático de "Páginas recientes" del sidebar.
 *
 * Escucha `Router.events` (NavigationEnd) y registra cada path navegado, junto
 * con su label resuelto del menú actual. Si la ruta no esta en el menu (ej.
 * detalle `/clientes/123`), tomamos el padre (`/clientes`) y usamos su label.
 *
 * Rutas que NO se trackean (configurable via SKIP_PATHS):
 *   - `/auth/*`  — login, register, reset
 *   - `/legacy/*` — placeholder de modulos no migrados (no son destinos validos)
 *   - `/design-preview*` — paginas internas de evaluacion de diseño
 *   - `/user-profile` — el usuario ya tiene atajo permanente al perfil
 *   - `/changelog` — meta
 *
 * Persistencia: localStorage `crm.sidebar.recents`.
 */
export interface RecentItem {
  /** Path completo navegado (sin queryParams). */
  path: string;
  /** Label resuelto del NavigationService al momento de la visita. */
  label: string;
  /** Icono legacy del item del menú padre. */
  icon?: string;
  /** Timestamp ms de la última visita. */
  visitedAt: number;
}

const STORAGE_KEY = 'crm.sidebar.recents';
const MAX_RECENTS = 5;

const SKIP_PATHS: ReadonlyArray<string | RegExp> = [
  /^\/auth(\/|$)/,
  /^\/legacy(\/|$)/,
  /^\/design-preview/,
  '/user-profile',
  '/changelog',
  '/home',  // Demasiado obvio para gastar un slot — el usuario lo abre permanentemente.
];

@Injectable({ providedIn: 'root' })
export class SidebarRecentsService {
  private router = inject(Router);
  private nav = inject(NavigationService);

  private _items = signal<RecentItem[]>(this.loadFromStorage());
  readonly items = computed(() => this._items());
  readonly count = computed(() => this._items().length);

  constructor() {
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.track(e.urlAfterRedirects));
  }

  /** Registra la visita a una URL. Filtra paths que no deberian aparecer. */
  private track(url: string): void {
    // Strip queryParams y fragment — el tracking es por ruta, no por filtro.
    const path = url.split('?')[0].split('#')[0];
    if (this.shouldSkip(path)) return;

    const meta = this.resolveMeta(path);
    if (!meta.label) return; // Sin label resuelto no vale la pena guardar.

    const next: RecentItem = {
      path,
      label: meta.label,
      icon: meta.icon,
      visitedAt: Date.now(),
    };

    // Dedup: si ya estaba en la lista, lo movemos al tope (visita mas reciente).
    const filtered = this._items().filter(r => r.path !== path);
    const updated = [next, ...filtered].slice(0, MAX_RECENTS);
    this._items.set(updated);
    this.persist();
  }

  private shouldSkip(path: string): boolean {
    return SKIP_PATHS.some(p =>
      typeof p === 'string' ? p === path : p.test(path),
    );
  }

  /**
   * Resuelve label e icono del item del menú correspondiente al path.
   * Si la ruta es un detalle (ej. `/clientes/123`), sube al padre (`/clientes`).
   */
  private resolveMeta(path: string): { label: string; icon?: string } {
    const items = this.nav.currentItems;
    // Match exacto primero.
    const exact = this.findByPath(items, path);
    if (exact) return { label: exact.text, icon: exact.icon };

    // Match parent: si la ruta tiene `/parent/x`, buscamos `/parent`.
    const parts = path.split('/').filter(Boolean);
    while (parts.length > 0) {
      parts.pop();
      const parentPath = '/' + parts.join('/');
      const parent = this.findByPath(items, parentPath);
      if (parent) return { label: parent.text, icon: parent.icon };
    }
    return { label: '' };
  }

  private findByPath(items: AppNavItem[] | undefined, path: string): AppNavItem | null {
    if (!items) return null;
    for (const it of items) {
      if (it.path === path) return it;
      const nested = this.findByPath(it.items, path);
      if (nested) return nested;
    }
    return null;
  }

  clear(): void {
    this._items.set([]);
    this.persist();
  }

  private loadFromStorage(): RecentItem[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((r): r is RecentItem =>
        r && typeof r.path === 'string'
        && typeof r.label === 'string'
        && typeof r.visitedAt === 'number',
      ).slice(0, MAX_RECENTS);
    } catch {
      return [];
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this._items()));
    } catch {
      // Ignorado — quota o bloqueo. El estado en memoria sigue funcionando.
    }
  }
}
